import { API } from 'aws-amplify';

// Standard API error interface
export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

// Common API response types
export interface RoleStatusResponse {
  roleStatus: string;
  connectionStatus: string;
  roleVersion?: string;
}

export interface SecurityGroupsGraphResponse {
  nodes: any[];
  ingress: any[];
  egress: any[];
  edges: any[];
}

export interface VPCRouteTablesGraphResponse {
  networkRoutingGraph: {
    nodes: any[];
    edges: any[];
  };
  networkAndInterfacesRoutingGraph: {
    nodes: any[];
    edges: any[];
  };
}

export interface TransitGatewayRouteTablesGraphResponse {
  graph: {
    nodes: any[];
    edges: any[];
  };
  computedGraph: {
    nodes: any[];
    edges: any[];
  };
}

export interface GlobalNetworkGraphResponse {
  nodes: any[];
  edges: any[];
}

// HTTP methods
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// Request configuration
export interface RequestConfig {
  queryStringParameters?: Record<string, any>;
  headers?: Record<string, string>;
  body?: any;
}

// Standard API service interface
export interface ApiService {
  get<T>(endpoint: string, config?: RequestConfig): Promise<T>;
  post<T>(endpoint: string, config?: RequestConfig): Promise<T>;
  put<T>(endpoint: string, config?: RequestConfig): Promise<T>;
  delete<T>(endpoint: string, config?: RequestConfig): Promise<T>;
}

// Standardized error handling
export class StandardApiError extends Error implements ApiError {
  public status?: number;
  public code?: string;
  public details?: any;

  constructor(
    message: string,
    status?: number,
    code?: string,
    details?: any
  ) {
    super(message);
    this.name = 'StandardApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Main API service implementation
class VizConApiService implements ApiService {
  private readonly apiName = 'VizConAPI';

  private sanitizeErrorMessage(message: string): string {
    // Remove file paths, URLs, endpoints, and other sensitive information
    return message
      .replace(/Error (getting|posting to|putting to|deleting) \/[^\s:]*/gi, 'Request failed')
      .replace(/\/[a-zA-Z0-9/_\-.]+/g, '[path]') // Remove any remaining paths
      .replace(/https?:\/\/[^\s]+/g, '[url]') // Remove URLs
      .replace(/file:\/\/[^\s]+/g, '[file]') // Remove file URLs
      .replace(/C:\\[^\s]+/g, '[path]') // Remove Windows paths
      .replace(/\/[a-zA-Z0-9/_-]+\.[a-zA-Z0-9]+/g, '[file]') // Remove file paths with extensions
      .trim();
  }

  private handleError(error: any, operation: string): never {
    let cause: any = '';
    let status: number | undefined;
    let errorData: any = undefined;

    if (error.response !== undefined) {
      cause = error.response.data;
      status = error.response.status;
      errorData = error.response.data; // Preserve full error data
    } else {
      cause = error.message || error.toString();
    }

    // Extract error message while preserving full data
    let errorMessage: string;
    if (typeof cause === 'object' && cause !== null) {
      if ('error' in cause && cause.error) {
        errorMessage = cause.error;
      } else if ('message' in cause && cause.message) {
        errorMessage = cause.message;
      } else {
        errorMessage = JSON.stringify(cause);
      }
    } else {
      errorMessage = String(cause);
    }

    // Sanitize the error message to remove paths and sensitive information
    const sanitizedMessage = this.sanitizeErrorMessage(errorMessage);

    // Create error with preserved data
    const apiError = new StandardApiError(
      sanitizedMessage || 'An error occurred while processing your request',
      status,
      error.code,
      errorData // Pass the full error data object
    );

    throw apiError;
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    try {
      return await API.get(this.apiName, endpoint, {
        queryStringParameters: config?.queryStringParameters,
        headers: config?.headers
      });
    } catch (error) {
      this.handleError(error, `getting ${endpoint}`);
    }
  }

  async post<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    try {
      return await API.post(this.apiName, endpoint, {
        body: config?.body,
        headers: config?.headers,
        queryStringParameters: config?.queryStringParameters
      });
    } catch (error) {
      this.handleError(error, `posting to ${endpoint}`);
    }
  }

  async put<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    try {
      return await API.put(this.apiName, endpoint, {
        body: config?.body,
        headers: config?.headers,
        queryStringParameters: config?.queryStringParameters
      });
    } catch (error) {
      this.handleError(error, `putting to ${endpoint}`);
    }
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    try {
      return await API.del(this.apiName, endpoint, {
        headers: config?.headers,
        queryStringParameters: config?.queryStringParameters
      });
    } catch (error) {
      this.handleError(error, `deleting ${endpoint}`);
    }
  }
}

// Export singleton instance
export const apiService: ApiService = new VizConApiService();

// Export for testing or custom implementations
export { VizConApiService };